import { computed } from 'vue'
import { useQuery } from '@vue/apollo-composable'

import {
  FileOwnersDocument,
  FileAuthorsDocument,
} from '@/shared/graphql/router/graphql'
import type { CopyrightFile } from '@/features/copyright/composables/useCopyrightFile'

export interface EntityListItem {
  id: string
  name: string
  avatarUrl: string
}

const queryOptions = {
  fetchPolicy: 'cache-and-network',
  nextFetchPolicy: 'cache-only',
  clientId: 'router',
} as const

export function useAllOwners(file: CopyrightFile) {
  const { result, loading } = useQuery(
    FileOwnersDocument,
    { id: file.copyright?.id || '' },
    queryOptions
  )

  const entities = computed<EntityListItem[]>(() => {
    const list: EntityListItem[] = []

    if ('id' in file.owner) {
      list.push({
        id: file.owner.id,
        name: file.owner.name,
        avatarUrl: file.owner.avatar.url,
      })
    }

    for (const edge of result.value?.file?.coOwners.edges ?? []) {
      if (edge.node.__typename === 'EntityWithRole') {
        const entity = edge.node.entity
        list.push({
          id: entity.id,
          name: entity.name,
          avatarUrl: entity.avatar.url,
        })
      }
    }

    return list
  })

  return { entities, loading }
}

export function useAllAuthors(file: CopyrightFile) {
  const { result, loading } = useQuery(
    FileAuthorsDocument,
    { id: file.copyright?.id || '' },
    queryOptions
  )

  const entities = computed<EntityListItem[]>(() => {
    const list: EntityListItem[] = []

    for (const edge of result.value?.file?.authors.edges ?? []) {
      if (edge.node.__typename === 'EntityWithRole') {
        const entity = edge.node.entity
        list.push({
          id: entity.id,
          name: entity.name,
          avatarUrl: entity.avatar.url,
        })
      }
    }

    return list
  })

  return { entities, loading }
}
